export type EventMediaType = 'image' | 'video';

export interface EventMediaItem {
  type: EventMediaType;
  url: string;
  position: number;
  thumbnail_url?: string | null;
}

const VIDEO_HOST_PATTERN = /(youtube\.com|youtu\.be|vimeo\.com|\.mp4($|\?)|\.webm($|\?))/i;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeType(value: unknown): EventMediaType | null {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'video') return 'video';
  if (normalized === 'image') return 'image';
  return null;
}

function normalizeUrl(value: unknown): string {
  return String(value || '').trim();
}

export function isLikelyVideoUrl(url?: string | null): boolean {
  const candidate = String(url || '').trim();
  if (!candidate) return false;
  return VIDEO_HOST_PATTERN.test(candidate);
}

export function normalizeEventMediaItems(
  mediaItems: unknown,
  fallbackImageUrl?: string | null,
): EventMediaItem[] {
  const parsed = Array.isArray(mediaItems)
    ? mediaItems
    : typeof mediaItems === 'string'
      ? (() => {
          try {
            const json = JSON.parse(mediaItems);
            return Array.isArray(json) ? json : [];
          } catch {
            return [];
          }
        })()
      : [];

  const normalized = parsed
    .map((item, index): EventMediaItem | null => {
      if (!isRecord(item)) return null;
      const url = normalizeUrl(item.url);
      if (!url) return null;
      const type =
        normalizeType(item.type) ??
        (isLikelyVideoUrl(url) ? 'video' : 'image');
      const rawPosition = Number(item.position);
      return {
        type,
        url,
        position: Number.isFinite(rawPosition) ? rawPosition : index,
        thumbnail_url: normalizeUrl(item.thumbnail_url || '') || null,
      };
    })
    .filter((item): item is EventMediaItem => Boolean(item))
    .sort((a, b) => a.position - b.position)
    .map((item, index) => ({ ...item, position: index }));

  if (normalized.length > 0) return normalized;

  const fallback = normalizeUrl(fallbackImageUrl);
  if (!fallback) return [];
  return [{ type: 'image', url: fallback, position: 0 }];
}

export function getPrimaryImageUrl(
  mediaItems: EventMediaItem[],
  fallbackImageUrl?: string | null,
): string | null {
  const firstImage = mediaItems.find((item) => item.type === 'image')?.url;
  if (firstImage) return firstImage;
  const fallback = normalizeUrl(fallbackImageUrl);
  return fallback || null;
}
